import { AppraisalQuestion } from '../useAppraisalFlow'
import { cn } from '@/utils/cn'

export interface OptionGridProps {
  question: AppraisalQuestion
  /** Selected option index as string, or undefined. */
  value: string | undefined
  onChange: (value: string | null) => void
}

export default function OptionGrid({
  question,
  value,
  onChange,
}: OptionGridProps) {
  const options = question.options ?? []
  const descriptions = question.optionDescriptions ?? []
  // options with an explanation (condition: "Neu — Originalverpackt und
  // ungeöffnet.") show it inside the selected button, like the product
  // configurator's condition question — that needs a full row per option
  const hasDescriptions = descriptions.some(Boolean)
  // compact sets (Ja/Nein, fineness stamps, storage sizes) sit side by side;
  // long labels (model names) keep a full row each
  const twoColumns =
    !hasDescriptions &&
    (options.length === 2 ||
      (options.length >= 4 && options.every((option) => option.length <= 14)))

  return (
    <div
      role="listbox"
      className={cn('grid gap-2', twoColumns ? 'grid-cols-2' : 'grid-cols-1')}
    >
      {options.map((option, index) => {
        const indexValue = String(index)
        const isSelected = value === indexValue
        const description = isSelected ? descriptions[index] : undefined

        return (
          <button
            key={option}
            type="button"
            role="option"
            aria-selected={isSelected}
            className={cn(
              'input-border w-full rounded-xl bg-static-white px-6 py-3',
              hasDescriptions ? 'text-left' : 'text-center',
              isSelected && 'outline outline-2 outline-brand-secondary',
            )}
            onClick={() => onChange(isSelected ? null : indexValue)}
          >
            <span
              className={cn(
                'flex flex-row gap-4',
                !hasDescriptions && 'justify-center',
              )}
            >
              <span className="text-l font-semibold text-primary">
                {option}
              </span>
              {description && (
                <span className="text-sm text-secondary font-normal pt-[3px]">
                  {description}
                </span>
              )}
            </span>
          </button>
        )
      })}
    </div>
  )
}
